import React, { useCallback, useEffect, useState } from "react";
import {
  API_BASE_URL,
  EMPTY_SETTINGS,
  compareWorkstreams,
  projectRequest,
  saveSettings,
  settingsRequest,
  workspaceRequest
} from "./settings/settingsUtils";
import {
  actionsStyle,
  cardStyle,
  checkboxGridStyle,
  checkboxStyle,
  dangerButtonStyle,
  githubMappingListStyle,
  gridStyle,
  headerStyle,
  helpStyle,
  inputStyle,
  labelStyle,
  linkButtonStyle,
  memberListStyle,
  memberPanelStyle,
  memberRowStyle,
  messageStyle,
  pageStyle,
  primaryButtonStyle,
  rankStyle,
  secondaryButtonStyle,
  sectionTitleStyle,
  smallSelectStyle,
  statusStyle,
  workstreamListStyle,
  workstreamRowStyle
} from "./settings/settingsStyles";
import BillingSection from "./settings/BillingSection";

const SettingsPage = ({ token }) => {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [workspace, setWorkspace] = useState(null);
  const [workstreamNames, setWorkstreamNames] = useState({});
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [jiraBaseUrl, setJiraBaseUrl] = useState("");
  const [jiraEmail, setJiraEmail] = useState("");
  const [jiraApiToken, setJiraApiToken] = useState("");
  const [jiraProjectKey, setJiraProjectKey] = useState("");
  const [githubRepositories, setGithubRepositories] = useState([]);
  const [githubMappings, setGithubMappings] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviteProjectIds, setInviteProjectIds] = useState([]);
  const isOwner = workspace?.current_role === "owner";
  const inviteLimitReached = Boolean(
    workspace && workspace.member_count >= workspace.member_limit
  );
  const callbackParams = new URLSearchParams(window.location.search);
  const hasPendingGitHubCallback = Boolean(
    callbackParams.get("installation_id") && callbackParams.get("state")
  );

  const loadGitHubRepositories = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/settings/github/repositories`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Could not load GitHub repositories.");
    setGithubRepositories(data.repositories || []);
  }, [token]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setSettings(data);
      setJiraBaseUrl(data.jira?.base_url || "");
      setJiraEmail(data.jira?.email || "");
      setJiraProjectKey(data.jira?.project_key || "");
      setGithubMappings(Object.fromEntries(
        (data.github?.mappings || []).map((mapping) => [mapping.project_id, String(mapping.repository_id)])
      ));
      if (data.github?.connected) await loadGitHubRepositories();
    } catch {
      setMessage("Could not load settings.");
    }
  }, [loadGitHubRepositories, token]);

  const loadWorkspace = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/workspace/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setWorkspace(data);
      setWorkstreamNames(Object.fromEntries((data.projects || []).map((project) => [project.id, project.name])));
    } catch {
      setMessage("Could not load workspace members.");
    }
  }, [token]);

  useEffect(() => {
    if (!hasPendingGitHubCallback) load();
    loadWorkspace();
  }, [hasPendingGitHubCallback, load, loadWorkspace]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const installationId = params.get("installation_id");
    const state = params.get("state");
    if (installationId && state) {
      const complete = async () => {
        setLoading(true);
        setMessage("Completing the GitHub connection...");
        try {
          await settingsRequest("/settings/github/complete", "POST", {
            installation_id: Number(installationId),
            state
          }, token);
          window.history.replaceState({}, "", "/settings");
          await load();
          setMessage("GitHub connected. Select a repository for each workstream.");
        } catch (error) {
          setMessage(error.message || "GitHub could not be connected.");
        } finally {
          setLoading(false);
        }
      };
      complete();
      return;
    }
    const githubResult = params.get("github");
    if (!githubResult) return;
    setMessage(
      githubResult === "connected"
        ? "GitHub connected. Select a repository for each workstream."
        : params.get("message") || "GitHub could not be connected."
    );
    window.history.replaceState({}, "", "/settings");
  }, [load, token]);

  const saveOpenAI = async (clear = false) => {
    setLoading(true);
    setMessage(clear ? "Removing OpenAI API key..." : "Saving OpenAI API key...");
    try {
      const data = await saveSettings({
        openai: {
          api_key: clear ? null : openaiApiKey,
          clear_api_key: clear
        }
      }, token);
      setSettings(data);
      setOpenaiApiKey("");
      setMessage(clear ? "OpenAI API key removed." : "OpenAI API key saved.");
    } catch (error) {
      setMessage(error.message || "Error saving OpenAI settings.");
    } finally {
      setLoading(false);
    }
  };

  const saveJira = async () => {
    setLoading(true);
    setMessage("Saving Jira settings...");
    try {
      const data = await saveSettings({
        openai: {},
        jira: {
          base_url: jiraBaseUrl,
          email: jiraEmail,
          api_token: jiraApiToken,
          project_key: jiraProjectKey
        }
      }, token);
      setSettings(data);
      setJiraApiToken("");
      setMessage("Jira settings saved.");
    } catch (error) {
      setMessage(error.message || "Error saving Jira settings.");
    } finally {
      setLoading(false);
    }
  };

  const connectGitHub = async () => {
    setLoading(true);
    setMessage("Opening GitHub...");
    try {
      const data = await settingsRequest("/settings/github/install-url", "GET", null, token);
      window.location.assign(data.url);
    } catch (error) {
      setMessage(error.message || "Could not start the GitHub connection.");
      setLoading(false);
    }
  };

  const saveGitHubMappings = async () => {
    setLoading(true);
    setMessage("Saving repository mappings...");
    try {
      const mappings = Object.entries(githubMappings)
        .filter(([, repositoryId]) => repositoryId)
        .map(([projectId, repositoryId]) => ({
          project_id: Number(projectId),
          repository_id: Number(repositoryId)
        }));
      const github = await settingsRequest("/settings/github/mappings", "PUT", { mappings }, token);
      setSettings((current) => ({ ...current, github }));
      setMessage("GitHub repository mappings saved.");
    } catch (error) {
      setMessage(error.message || "Could not save repository mappings.");
    } finally {
      setLoading(false);
    }
  };

  const refreshGitHubRepositories = async () => {
    setLoading(true);
    setMessage("Refreshing repositories...");
    try {
      await loadGitHubRepositories();
      setMessage("GitHub repositories refreshed.");
    } catch (error) {
      setMessage(error.message || "Could not refresh GitHub repositories.");
    } finally {
      setLoading(false);
    }
  };

  const disconnectGitHub = async () => {
    if (!window.confirm("Disconnect GitHub and remove Taskit's repository access?")) return;
    setLoading(true);
    setMessage("Disconnecting GitHub...");
    try {
      const github = await settingsRequest("/settings/github", "DELETE", null, token);
      setSettings((current) => ({ ...current, github }));
      setGithubRepositories([]);
      setGithubMappings({});
      setMessage("GitHub disconnected.");
    } catch (error) {
      setMessage(error.message || "Could not disconnect GitHub.");
    } finally {
      setLoading(false);
    }
  };

  const toggleInviteProject = (projectId) => {
    setInviteProjectIds((current) => (
      current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : [...current, projectId]
    ));
  };

  const updateWorkstreamName = async (project) => {
    const name = (workstreamNames[project.id] || "").trim();
    if (!name) {
      setMessage("Workstream name cannot be empty.");
      return;
    }
    setLoading(true);
    setMessage("Saving workstream...");
    try {
      await projectRequest(`/projects/${project.id}`, "PUT", { name }, token);
      await loadWorkspace();
      setMessage("Workstream updated.");
    } catch (error) {
      setMessage(error.message || "Error updating workstream.");
    } finally {
      setLoading(false);
    }
  };

  const moveWorkstream = async (projectId, direction) => {
    const projects = [...(workspace?.projects || [])].sort(compareWorkstreams);
    const index = projects.findIndex((project) => project.id === projectId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= projects.length) return;
    const reordered = [...projects];
    [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
    setLoading(true);
    setMessage("Updating workstream priority...");
    try {
      await projectRequest("/projects/reorder", "PUT", { project_ids: reordered.map((project) => project.id) }, token);
      await loadWorkspace();
      setMessage("Workstream priority updated.");
    } catch (error) {
      setMessage(error.message || "Error updating workstream priority.");
    } finally {
      setLoading(false);
    }
  };

  const inviteMember = async () => {
    setLoading(true);
    setMessage("Sending invite...");
    try {
      const data = await workspaceRequest("/workspace/invites", "POST", {
        email: inviteEmail,
        role: inviteRole,
        project_ids: inviteProjectIds
      }, token);
      setWorkspace(data);
      setInviteEmail("");
      setInviteRole("editor");
      setInviteProjectIds([]);
      setMessage("Invite sent.");
    } catch (error) {
      setMessage(error.message || "Error sending invite.");
    } finally {
      setLoading(false);
    }
  };

  const updateMember = async (member, changes = {}) => {
    setLoading(true);
    setMessage("Updating member...");
    try {
      const data = await workspaceRequest(`/workspace/members/${member.id}`, "PUT", {
        role: changes.role || member.role,
        project_ids: changes.project_ids || member.project_ids
      }, token);
      setWorkspace(data);
      setMessage("Member updated.");
    } catch (error) {
      setMessage(error.message || "Error updating member.");
    } finally {
      setLoading(false);
    }
  };

  const cancelInvite = async (inviteId) => {
    setLoading(true);
    setMessage("Canceling invite...");
    try {
      const data = await workspaceRequest(`/workspace/invites/${inviteId}`, "DELETE", null, token);
      setWorkspace(data);
      setMessage("Invite canceled.");
    } catch (error) {
      setMessage(error.message || "Error canceling invite.");
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (member) => {
    if (!window.confirm(`Remove ${member.email} from this workspace?`)) return;
    setLoading(true);
    setMessage("Removing member...");
    try {
      const data = await workspaceRequest(`/workspace/members/${member.id}`, "DELETE", null, token);
      setWorkspace(data);
      setMessage("Member removed.");
    } catch (error) {
      setMessage(error.message || "Error removing member.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={{ margin: "0 0 8px" }}>Settings</h1>
          <p style={{ margin: 0, color: "#94a3b8" }}>
            Manage AI credentials and external integrations for this workspace.
          </p>
        </div>
      </div>

      {message && <div style={messageStyle}>{message}</div>}

      <div style={gridStyle}>
        <BillingSection token={token} />

        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>OpenAI</h2>
          <p style={helpStyle}>
            Add the workspace OpenAI API key. Owners manage this key and Editors use it for AI generation.
          </p>
          <div style={statusStyle}>
            <strong>Status</strong>
            <span>{settings.openai?.has_api_key ? `Saved (${settings.openai.key_preview})` : "No workspace key saved"}</span>
          </div>
          <label style={labelStyle}>OpenAI API Key</label>
          <input
            type="password"
            value={openaiApiKey}
            onChange={(event) => setOpenaiApiKey(event.target.value)}
            placeholder="sk-..."
            style={inputStyle}
          />
          <div style={actionsStyle}>
            <button style={primaryButtonStyle} onClick={() => saveOpenAI(false)} disabled={loading || !openaiApiKey.trim()}>
              {loading ? "Saving..." : "Save OpenAI key"}
            </button>
            <button style={secondaryButtonStyle} onClick={() => saveOpenAI(true)} disabled={loading || !settings.openai?.has_api_key}>
              Remove key
            </button>
          </div>
        </section>

        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>Jira</h2>
          <p style={helpStyle}>
            Configure the shared Jira project used by every workstream in Task Studio and Presentation Studio.
          </p>
          <label style={labelStyle}>Jira Base URL</label>
          <input value={jiraBaseUrl} onChange={(event) => setJiraBaseUrl(event.target.value)} placeholder="https://your-company.atlassian.net" style={inputStyle} />
          <label style={labelStyle}>Jira Email</label>
          <input type="email" value={jiraEmail} onChange={(event) => setJiraEmail(event.target.value)} placeholder="you@company.com" style={inputStyle} />
          <label style={labelStyle}>Jira API Token</label>
          <input type="password" value={jiraApiToken} onChange={(event) => setJiraApiToken(event.target.value)} placeholder="Enter a token to save or update" style={inputStyle} />
          <label style={labelStyle}>Jira Project Key</label>
          <input value={jiraProjectKey} onChange={(event) => setJiraProjectKey(event.target.value)} placeholder="PR" style={inputStyle} />
          <button style={primaryButtonStyle} onClick={saveJira} disabled={loading || !jiraBaseUrl || !jiraEmail || !jiraApiToken || !jiraProjectKey}>
            {loading ? "Saving..." : "Save Jira settings"}
          </button>
        </section>

        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>GitHub</h2>
          <p style={helpStyle}>
            Install the Taskit GitHub App on your account or organization, then choose which repository belongs to each workstream.
          </p>
          <div style={statusStyle}>
            <strong>Status</strong>
            <span>
              {settings.github?.connected
                ? `Connected to ${settings.github.account_login}`
                : settings.github?.configured ? "Not connected" : "Not configured on this server"}
            </span>
          </div>
          {!settings.github?.configured && (
            <p style={helpStyle}>Add the Taskit GitHub App credentials to the server environment before connecting.</p>
          )}
          {!settings.github?.connected ? (
            <button style={primaryButtonStyle} onClick={connectGitHub} disabled={loading || !settings.github?.configured}>
              Connect GitHub
            </button>
          ) : (
            <>
              <div style={githubMappingListStyle}>
                {[...(workspace?.projects || [])].sort(compareWorkstreams).map((project) => (
                  <label key={project.id} style={labelStyle}>
                    {project.name}
                    <select
                      aria-label={`${project.name} repository`}
                      value={githubMappings[project.id] || ""}
                      onChange={(event) => setGithubMappings((current) => ({
                        ...current,
                        [project.id]: event.target.value
                      }))}
                      style={{ ...inputStyle, marginTop: "8px" }}
                    >
                      <option value="">No repository</option>
                      {githubRepositories.map((repository) => (
                        <option key={repository.id} value={repository.id}>
                          {repository.full_name}{repository.private ? " (private)" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <div style={actionsStyle}>
                <button style={primaryButtonStyle} onClick={saveGitHubMappings} disabled={loading}>
                  Save repository mappings
                </button>
                <button style={secondaryButtonStyle} onClick={refreshGitHubRepositories} disabled={loading}>
                  Refresh repositories
                </button>
                {settings.github.manage_url && (
                  <a href={settings.github.manage_url} target="_blank" rel="noreferrer" style={linkButtonStyle}>
                    Manage GitHub access
                  </a>
                )}
                <button style={dangerButtonStyle} onClick={disconnectGitHub} disabled={loading}>
                  Disconnect
                </button>
              </div>
            </>
          )}
        </section>

        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>Workstreams</h2>
          <p style={helpStyle}>
            Rename workstreams and set their priority. Workspace-level views use this order before each workstream backlog order.
          </p>
          {!isOwner && <p style={helpStyle}>Only Owners can rename or reorder workstreams.</p>}
          <div style={workstreamListStyle}>
            {[...(workspace?.projects || [])].sort(compareWorkstreams).map((project, index, list) => (
              <div key={project.id} style={workstreamRowStyle}>
                <span style={rankStyle}>{index + 1}</span>
                <input
                  value={workstreamNames[project.id] ?? project.name}
                  onChange={(event) => setWorkstreamNames((current) => ({ ...current, [project.id]: event.target.value }))}
                  disabled={!isOwner}
                  style={inputStyle}
                />
                <button style={secondaryButtonStyle} onClick={() => moveWorkstream(project.id, -1)} disabled={loading || !isOwner || index === 0}>
                  Up
                </button>
                <button style={secondaryButtonStyle} onClick={() => moveWorkstream(project.id, 1)} disabled={loading || !isOwner || index === list.length - 1}>
                  Down
                </button>
                <button style={primaryButtonStyle} onClick={() => updateWorkstreamName(project)} disabled={loading || !isOwner || (workstreamNames[project.id] ?? project.name).trim() === project.name}>
                  Save
                </button>
              </div>
            ))}
          </div>
        </section>

        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>Members</h2>
          <p style={helpStyle}>
            Invite workspace members, choose their role, and select which workstreams they can access.
          </p>
          <div style={statusStyle}>
            <strong>Seats</strong>
            <span>{workspace ? `${workspace.member_count} / ${workspace.member_limit} invited members` : "Loading..."}</span>
          </div>
          {!isOwner && <p style={helpStyle}>Only Owners can invite people or change access.</p>}
          {isOwner && inviteLimitReached && (
            <p style={helpStyle}>
              {workspace.member_limit === 0
                ? "Free includes no invited users. Upgrade this workspace to Pro to invite up to 10 people."
                : "This workspace has reached its invited-user limit."}
            </p>
          )}
          {isOwner && !inviteLimitReached && (
            <div style={memberPanelStyle}>
              <label style={labelStyle}>Invite Email</label>
              <input type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} placeholder="person@company.com" style={inputStyle} />
              <label style={labelStyle}>Role</label>
              <select value={inviteRole} onChange={(event) => setInviteRole(event.target.value)} style={inputStyle}>
                <option value="owner">Owner</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <div style={labelStyle}>Workstream Access</div>
              <div style={checkboxGridStyle}>
                {(workspace?.projects || []).map((project) => (
                  <label key={project.id} style={checkboxStyle}>
                    <input type="checkbox" checked={inviteProjectIds.includes(project.id)} onChange={() => toggleInviteProject(project.id)} />
                    {project.name}
                  </label>
                ))}
              </div>
              <button style={primaryButtonStyle} onClick={inviteMember} disabled={loading || !inviteEmail || !inviteProjectIds.length}>
                {loading ? "Sending..." : "Send invite"}
              </button>
            </div>
          )}

          <div style={memberListStyle}>
            {(workspace?.members || []).map((member) => (
              <div key={member.id} style={memberRowStyle}>
                <div>
                  <strong>{member.email}</strong>
                  <div style={{ color: "#94a3b8", fontSize: "13px" }}>{member.status}</div>
                </div>
                <select value={member.role} disabled={!isOwner || member.role === "owner"} onChange={(event) => updateMember(member, { role: event.target.value })} style={smallSelectStyle}>
                  <option value="owner">Owner</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
                <div style={checkboxGridStyle}>
                  {(workspace?.projects || []).map((project) => (
                    <label key={project.id} style={checkboxStyle}>
                      <input
                        type="checkbox"
                        disabled={!isOwner || member.role === "owner"}
                        checked={member.project_ids.includes(project.id)}
                        onChange={() => {
                          const projectIds = member.project_ids.includes(project.id)
                            ? member.project_ids.filter((id) => id !== project.id)
                            : [...member.project_ids, project.id];
                          updateMember(member, { project_ids: projectIds });
                        }}
                      />
                      {project.name}
                    </label>
                  ))}
                </div>
                {isOwner && member.role !== "owner" && (
                  <button style={dangerButtonStyle} onClick={() => removeMember(member)} disabled={loading}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            {(workspace?.invites || []).map((invite) => (
              <div key={`invite-${invite.id}`} style={memberRowStyle}>
                <div>
                  <strong>{invite.invited_email}</strong>
                  <div style={{ color: "#facc15", fontSize: "13px" }}>Invite pending · {invite.role}</div>
                </div>
                {isOwner && <button style={secondaryButtonStyle} onClick={() => cancelInvite(invite.id)}>Cancel</button>}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;
